<template>
  <div class="search-page">
    <div
      class="filter-container"
      :class="{expanded: filterOptionsExpanded}"
    >
      <div 
        class="filter-container-header"
        @click="filterOptionsExpanded = !filterOptionsExpanded"
      >
        Filter Results

        <span :class="filterOptionsExpanded ? 'up-arrow' : 'down-arrow'" />
      </div>
      <div
        class="filter-container-content"
        :class="{
          expanded: filterOptionsExpanded,
          collapsed: !filterOptionsExpanded,
        }"
      >
        <div class="input-group">
          <TextInput
            v-model="searchTerm"
            label="Search Term"
          />
        </div>
        <div class="button-wrapper">
          <button
            class="clear"
            @click="clearSearchForm"
          >
            Clear
          </button>
          <button
            class="search"
            @click="search"
          >
            Search
          </button>
        </div>
      </div>
    </div>
    <div class="results-container-header">
      <div class="results-container-header-text">
        Manufacturers
      </div>
      <router-link
        class="create"
        to="/manufacturer/0"
      >
        Create
      </router-link>
    </div>
    <LoadingPulse
      :loading="loading"
      class="loading-spinner"
    />
    <div
      v-if="!loading"
      class="overflow-wrapper"
    >
      <table>
        <thead>
          <tr>
            <th @click="sortBy('Name')">
              <div>
                Name
                <span
                  v-show="sortField === 'Name'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('PhoneNumber')">
              <div>
                Phone Number
                <span
                  v-show="sortField === 'PhoneNumber'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('Email')">
              <div>
                Email
                <span
                  v-show="sortField === 'Email'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('Website')">
              <div>
                Website
                <span
                  v-show="sortField === 'Website'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th class="no-sort" />
          </tr>
        </thead>
        <tbody v-if="manufacturers.length > 0">
          <tr
            v-for="manufacturer in manufacturers"
            :key="manufacturer.id"
            class="clickable"
            @click="onTableRowClicked(manufacturer.id)"
          >
            <td>{{ manufacturer.name }}</td>
            <td>{{ $methods.formatPhoneNumber(manufacturer.phoneNumber) }}</td>
            <td>{{ manufacturer.email }}</td>
            <td>{{ manufacturer.website }}</td>
            <td class="delete">
              <TrashCanSvg @click.stop="onDeleteClicked(manufacturer.id)" />
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td
              :colspan="5"
              class="message"
            >
              No manufacturers were found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Paginator
      v-if="!loading"
      class="paginator"
      :total-results="totalResults"
      :page-number="pageNumber"
      :page-size="pageSize"
      @page-change="onPageChanged"
      @page-size-change="onPageSizeChanged"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import Paginator from '@/components/shared/Paginator.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { ManufacturerService } from '@/services/manufacturer.service'
import { Manufacturer } from '@/@types/responses/manufacturer/Manufacturer'
import Toaster from '@meforma/vue-toaster'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Manufacturers',
  components: {
    LoadingPulse,
    Paginator,
    TextInput,
    TrashCanSvg
  },
  setup() {
    const manufacturerService = inject(PROVIDER_CONSTANTS.manufacturerService) as ManufacturerService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      manufacturerService,
      toast
    }
  },
  data() {
    return {
      ascending: true,
      filterOptionsExpanded: true,
      loading: true,
      manufacturers: [] as Manufacturer[],
      pageNumber: 1,
      pageSize: 10,
      searchTerm: '',
      sortField: 'Name',
      totalResults: 0
    }
  },
  mounted() {
    window.addEventListener("popstate", this.parseQueryStringAndSearch)

    this.parseQueryStringAndSearch()
  },
  unmounted() {
    window.removeEventListener('popstate', this.parseQueryStringAndSearch)
  },
  methods: {
    clearSearchForm(): void{
      this.searchTerm = ''
      this.search()
    },
    getBooleanParameterByName(name: string): boolean | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? parameter === 'true' : undefined
    },
    getNumericParameterByName(name: string): number | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? Number(parameter) : undefined
    },
    getStringParameterByName(name: string): string | undefined {
      const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)")
      const results = regex.exec(window.location.href)

      if (!results || !results[2])
        return undefined
      
      return decodeURIComponent(results[2].replace(/\+/g, " "));
    },
    onDeleteClicked(id: number): void {
      this.manufacturerService.deleteManufacturer(id).then(() => {
        if ((this.manufacturers.length - 1) === 0 && this.pageNumber > 1)
          this.onPageChanged(this.pageNumber - 1)
        else
          this.searchManufacturers()

        this.toast.success('Manufacturer deleted successfully.')
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the manufacturer. Please try again.')
        }
      })
    },
    onPageChanged(pageNumber: number): void {
      this.pageNumber = pageNumber
      this.searchManufacturers()
    },
    onPageSizeChanged(pageSize: number): void {
      this.pageSize = pageSize
      this.search()
    },
    onTableRowClicked(id: number): void {
      this.$router.push('/manufacturer/' + id)
    },
    parseQueryStringAndSearch(): void {
      const ascendingFromUrl = this.getBooleanParameterByName("a")
      this.ascending = ascendingFromUrl !== undefined ? ascendingFromUrl : this.ascending

      const pageNumberFromUrl = this.getNumericParameterByName("pn")
      this.pageNumber = pageNumberFromUrl !== undefined ? pageNumberFromUrl : this.pageNumber

      const pageSizeFromUrl = this.getNumericParameterByName("ps")
      this.pageSize = pageSizeFromUrl !== undefined ? pageSizeFromUrl : this.pageSize

      const searchTermFromUrl = this.getStringParameterByName("st")
      this.searchTerm = searchTermFromUrl !== undefined ? searchTermFromUrl : this.searchTerm

      const sortFieldFromUrl = this.getStringParameterByName("sf")
      this.sortField = sortFieldFromUrl !== undefined ? sortFieldFromUrl : this.sortField

      this.searchManufacturers()
    },
    search(): void {
      this.pageNumber = 1
      this.searchManufacturers()
    },
    searchManufacturers(): void {
      this.loading = true
      this.updateUrl()
      this.manufacturerService.searchManufacturers(this.searchTerm, this.pageNumber, this.pageSize, this.sortField, this.ascending).then(pagingResponse => {
        this.totalResults = pagingResponse.totalResults
        this.manufacturers = pagingResponse.results
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the manufacturers. Try reloading the page.')
        }
      })
    },
    sortBy(sortField: string): void {
      if (this.sortField === sortField)
        this.ascending = !this.ascending
      else
        this.sortField = sortField
      
      this.searchManufacturers()
    },
    updateUrl(): void {
      let newQueryString = `?a=${this.ascending}&pn=${this.pageNumber}&ps=${this.pageSize}&sf=${this.sortField}`

      if (this.searchTerm !== '')
        newQueryString = newQueryString + `&st=${encodeURIComponent(this.searchTerm)}`
      
      this.$router.replace('/manufacturers' + newQueryString)
    }
  }
})
</script>

<style scoped lang="scss">
.search-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 15px;

  @include sac-search-page;
}
</style>
